import { useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";

// react-square-web-payments-sdk@3.2.1 swallows a cancelled/failed 3-D Secure
// challenge internally (payments.verifyBuyer() rejects, the library only
// console.errors it) — cardTokenizeResponseReceived never fires for that
// path, so without a guard the buyer sees the Pay button silently re-enable
// with no feedback.
//
// Instead of a blind timeout (which fired mid-challenge for buyers who took
// longer than it to fetch an OTP), the guard watches the elements Square
// injects into the page. While the challenge overlay is visible we stay silent
// for minutes; when it disappears without our tokenize callback arriving
// shortly after, that IS the swallowed cancel/failure — say so. If nothing at
// all happens (no 3-D Secure element, no callback — e.g. verifyBuyer died on a
// network error), give a generic nudge. Each exit reports its own reason: the
// three ends look identical to the buyer but need different answers from us.
// Both 3-D Secure steps come out of the same SDK helper, which builds a
// <div id="${baseID}-modal" class="square_threeds_modal"> holding an
// <iframe id="${baseID}-iframe" class="square_threeds_iframe">. The baseID is
// the whole difference between them, so it is what we identify them by.
const CHALLENGE_BASE_ID = "sq-threeds-challenge-flow";
const METHOD_BASE_ID_PREFIX = "sq-threeds-3ds-method-";
const GUARD_POLL_MS = 2000;
// Callback normally lands ~2-3s after a successful challenge closes; 5s
// keeps a real success from flashing the failure toast first.
const GUARD_GRACE_MS = 5000;
// Two very different waits. With the challenge on screen the buyer may
// legitimately take minutes (fetching an OTP on another device), so the
// window has to be long — but it does end: a challenge nobody answers would
// otherwise keep the guard silent for as long as the tab is open, and that
// abandoned checkout would reach neither the buyer nor onFailure. With
// nothing on screen the only thing outstanding is tokenize()+verifyBuyer(),
// which lands in seconds or has already died inside the SDK — waiting minutes
// there hides a dead checkout from the buyer and from us.
//
// The challenge window is Square's own (timeoutSeconds: 610 on the SDK's
// challenge-flow iframe) plus a margin, deliberately behind it rather than
// ahead: when Square gives up it destroys the modal, and the "challenge
// disappeared" path below reports that as incomplete. A shorter window of our
// own would only ever fire while Square is still willing to accept an answer —
// a buyer who typed the code at 5:30 would get payment_failed and then a
// successful charge. The margin covers the up-to-one-poll lag between the SDK
// starting its timer and us first seeing the modal; if it ever does fire, the
// SDK's own timeout did not, and something is stuck.
const SQUARE_CHALLENGE_TIMEOUT_MS = 610 * 1000;
const GUARD_CHALLENGE_MAX_MS = SQUARE_CHALLENGE_TIMEOUT_MS + 30 * 1000;
const GUARD_IDLE_MAX_MS = 30 * 1000;

// Whether the browser is actually painting an element. Note what this is NOT
// used for: telling the fingerprint step from the challenge. The only thing
// that hides the fingerprint modal is a stylesheet the SDK inserts and forgets
// — it appends a <link> and drops the load promise on the floor — so until
// that CSS arrives, or forever if a CSP or a blocker eats it, the hidden step
// reads back as visible with opacity 1. A guard that trusted the paint would
// take the fingerprint for a challenge, then call its scripted self-destruct at
// 11s a buyer walking out on the bank, and clear itself moments before the real
// challenge appears.
const isPainted = (el) => {
	if (!el?.isConnected) return false;
	const style = window.getComputedStyle(el);
	return style.visibility !== "hidden" && style.display !== "none" && style.opacity !== "0";
};

// The fingerprint (3ds-method) step, by things the SDK writes in code rather
// than in CSS: its generated baseID, and the inline visibility:hidden that the
// builder puts on every 3-D Secure iframe and only the challenge's onShow ever
// undoes.
const isMethodStep = (modal) => {
	if (modal.id.startsWith(METHOD_BASE_ID_PREFIX)) return true;
	const iframe = modal.querySelector("iframe");
	return Boolean(iframe) && iframe.style.visibility === "hidden";
};

// Is the bank's challenge on screen? Answered by identity, because these ids
// and class names are undocumented SDK internals and a rename must not make the
// answer false forever — that would drop the "no response" toast on top of a
// live challenge, for exactly the buyers this guard exists for. Hence four
// independent challenge-only marks, each enough on its own:
//   1. data-challenge-displayed="VISIBLE" — set by the challenge's onShow and
//      rewritten to REMOVED on destroy; the fingerprint step has no onShow.
//   2/3. the ids built from the challenge baseID.
//   4. square_threeds_modal__show — also added by that same onShow.
const isChallengeVisible = () => {
	if (document.querySelector('iframe[data-challenge-displayed="VISIBLE"]')) return true;
	if (document.getElementById(`${CHALLENGE_BASE_ID}-modal`)) return true;
	if (document.getElementById(`${CHALLENGE_BASE_ID}-iframe`)) return true;
	if (document.querySelector(".square_threeds_modal__show")) return true;
	// All four renamed at once: only the shared modal class is left, so the
	// fingerprint has to be excluded some other way. isPainted joins in here
	// and only here, as an extra AND — it can drop a modal out of the answer,
	// never promote one into it.
	return [...document.querySelectorAll("div.square_threeds_modal")].some(
		(modal) => !isMethodStep(modal) && isPainted(modal)
	);
};

// Coarser than the signals above, and deliberately so: it answers "the SDK is
// still mid-flow", not "the buyer can see something". Every 3-D Secure step is
// a <div id="${baseID}-modal"> holding an <iframe class="square_threeds_iframe">
// — "sq-threeds-challenge-flow" for the challenge, "sq-threeds-3ds-method-<rnd>"
// for the fingerprint step — and the SDK's destroy() removes both, so while one
// is in the DOM something is still running. Scoped to div/iframe on purpose:
// the SDK also parks a <link id="sq-threeds-modal-styles"> in <head> for the
// life of the page, and matching that would silence the guard forever.
const SQUARE_3DS_SELECTOR = 'div[id^="sq-threeds-"], iframe.square_threeds_iframe';

const isSquare3dsRunning = () => Boolean(document.querySelector(SQUARE_3DS_SELECTOR));

export function buildVerificationDetails({ user }) {
	// intent STORE, no amount: the token is consumed by cardsApi.createCard
	// (card on file for the subscription), which is the STORE flow in
	// Square's docs — the actual charge is made later by Square's own
	// subscription billing, never by us with this token.
	return {
		intent: "STORE",
		billingContact: {
			givenName: user?.firstName || undefined,
			familyName: user?.lastName || undefined,
			email: user?.email || undefined,
		},
	};
}

// `onFailure(reason)` is called alongside every toast this guard raises. All
// three paths below end the payment WITHOUT cardTokenizeResponseReceived ever
// firing, so a caller tracking conversions cannot see them any other way — to
// analytics they would look like a buyer who simply walked away. The reasons
// are distinct because the fixes are: 3ds_challenge_incomplete (buyer met the
// bank and it did not finish), 3ds_challenge_timeout (challenge opened and was
// never answered), 3ds_no_response (nothing ever came back from Square).
// One attempt can report twice, and only in this order: 3ds_no_response is a
// read of the moment (nothing had come back yet on a slow connection), not a
// verdict on the attempt, so a challenge that opens after it is still watched
// and still gets its own report. A caller counting attempts rather than events
// has to de-duplicate; the toast does not, only one is ever on screen.
// Optional: the five other checkout pages pass nothing and behave as before.
export function useSquareVerificationGuard({ notify, onFailure = () => {} }) {
	const stateRef = useRef({
		timer: null,
		graceTimer: null,
		deadline: 0,
		sawChallenge: false,
		idleNoticed: false,
		noticeId: null,
	});

	// Take back whatever we last said, leaving the guard itself running.
	// react-toastify pauses a toast that was raised while the document had no
	// focus (pauseOnFocusLoss, checked with document.hasFocus() as the toast
	// mounts), which is precisely where our failure toast is raised: the buyer
	// is in their banking app fetching the one-time code. Left alone it is still
	// on screen when they come back — possibly to a payment that completed in
	// the meantime, so a red error over a successful sale. Only ever with an id
	// we were given: toast.dismiss() with none would take down unrelated toasts
	// as well. And always before storing a new id: an overwritten one can never
	// be dismissed again.
	const dismissNotice = useCallback(() => {
		const { noticeId } = stateRef.current;
		if (noticeId !== null && noticeId !== undefined) {
			stateRef.current.noticeId = null;
			toast.dismiss(noticeId);
		}
	}, []);

	const clearGuard = useCallback(() => {
		window.clearTimeout(stateRef.current.timer);
		window.clearTimeout(stateRef.current.graceTimer);
		stateRef.current.timer = null;
		stateRef.current.graceTimer = null;
		// Whatever ends the guard (the tokenize callback landing, a fresh Pay
		// click, the page unmounting) ends the message too.
		dismissNotice();
	}, [dismissNotice]);

	const tick = useCallback(() => {
		const state = stateRef.current;
		if (isChallengeVisible()) {
			// Challenge is on screen — the buyer may legitimately take minutes.
			// A toast scheduled by the branch below belongs to an overlay that
			// only looked closed (a redraw, or the step between two 3-D Secure
			// screens): drop it instead of firing it over a live challenge.
			window.clearTimeout(state.graceTimer);
			state.graceTimer = null;
			if (!state.sawChallenge) {
				// Once per appearance. Pushing the deadline forward on every
				// poll is what made GUARD_CHALLENGE_MAX_MS unreachable; a
				// second challenge screen is progress, so it earns a new
				// window of its own.
				state.sawChallenge = true;
				state.deadline = Date.now() + GUARD_CHALLENGE_MAX_MS;
				// This challenge IS the response the idle notice below said
				// never came: retract it. It is false from here on, and its id
				// has to be released before this branch stores one of its own.
				dismissNotice();
			} else if (Date.now() >= state.deadline) {
				// Nobody is answering this challenge. The toast may well be
				// covered by the overlay itself, but onFailure is the point:
				// otherwise this checkout ends with nothing recorded anywhere.
				onFailure("3ds_challenge_timeout");
				// Every notify() below keeps its return value: the checkout pages
				// all pass a toast.error wrapper, so it is the react-toastify id
				// clearGuard needs to retract the message.
				state.noticeId = notify("Card verification is taking too long. Please try again.");
				return;
			}
			state.timer = window.setTimeout(tick, GUARD_POLL_MS);
			return;
		}
		if (state.sawChallenge) {
			// Challenge just closed. A success reaches our tokenize callback
			// (which calls clearGuard) within a couple of seconds; if it
			// doesn't, this was the swallowed cancel/failure.
			state.sawChallenge = false;
			state.graceTimer = window.setTimeout(() => {
				clearGuard();
				// Cancel and hard failure are indistinguishable from here — the
				// SDK swallows both — so the reason says "incomplete", not why.
				onFailure("3ds_challenge_incomplete");
				// After clearGuard, so the id survives to the next one.
				state.noticeId = notify("Card verification didn't complete. Please try again.");
			}, GUARD_GRACE_MS);
		} else if (isSquare3dsRunning()) {
			// Nothing on screen, but the SDK has not finished: the fingerprint
			// (3ds-method) iframe runs hidden first with its own 11s timeout,
			// and the AReq/ARes round trip follows before the challenge is
			// painted. On a slow mobile connection that prologue can outlast
			// the idle window — firing here would tell the buyer nothing came
			// back seconds before the challenge appears.
			state.deadline = Date.now() + GUARD_IDLE_MAX_MS;
		} else if (!state.graceTimer && !state.idleNoticed && Date.now() >= state.deadline) {
			// Said once per attempt, and then we keep watching. The first
			// tokenize()+verifyBuyer() round trip can outrun this window before
			// Square has painted anything at all — no element in the DOM to push
			// the deadline back — and returning here left the challenge that
			// followed unguarded for the rest of the attempt: its cancel would
			// reach neither the buyer nor onFailure. The latch is what makes
			// polling on safe: this deadline is now permanently in the past, so
			// an unlatched branch would restate itself every GUARD_POLL_MS.
			state.idleNoticed = true;
			onFailure("3ds_no_response");
			state.noticeId = notify(
				"We didn't get a response from the payment service. Please try again."
			);
		}
		// Polling continues through the grace window, and past the notice above,
		// on purpose: a challenge that comes back cancels the pending toast, and
		// a challenge that arrives late is still watched. The loop ends where it
		// always did — the two challenge reports above, or clearGuard when the
		// callback lands, the buyer clicks Pay again, or the page unmounts.
		state.timer = window.setTimeout(tick, GUARD_POLL_MS);
	}, [clearGuard, dismissNotice, notify, onFailure]);

	const startGuard = useCallback((e) => {
		// The capture handler sits on a wrapper div (the SDK's own click
		// handler stopPropagation()s before React's synthetic onClick can
		// run) — but only a click on the actual Pay button should arm the
		// guard, not a stray click anywhere in the wrapper.
		if (e?.target?.closest && !e.target.closest("#rswp-card-button")) return;
		clearGuard();
		stateRef.current.sawChallenge = false;
		stateRef.current.idleNoticed = false;
		stateRef.current.deadline = Date.now() + GUARD_IDLE_MAX_MS;
		stateRef.current.timer = window.setTimeout(tick, GUARD_POLL_MS);
	}, [clearGuard, tick]);

	// react-toastify queues toasts fired with no container mounted and
	// replays them on the next page that has one — without this cleanup an
	// abandoned checkout could pop a payment error on an unrelated screen.
	useEffect(() => clearGuard, [clearGuard]);

	return { startGuard, clearGuard };
}
